// store/modules/requests.js
import axios from "axios";
import Vue from "vue";
import moment from "moment";

const getEmptyDraft = (partnerId) => ({
  partnerId: partnerId || null,
  companyId: null,

  companyName: "",
  companyLocation: "",
  contactPersonName: "",
  contactPersonEmail: "",
  contactPersonPhone: "",

  trainingTypes: "",
  participantCount: 1,
  priceIndication: "",

  toyotaRepName: "",
  toyotaRepPhone: "",
  toyotaRepEmail: "",

  additionalInfo: "",
});

export default {
  namespaced: true,

  state: () => ({
    draft: getEmptyDraft(null),
    isSaving: false,
    saveError: null,
    items: [],
    isLoading: false,
    loadError: null,

    requestLookup: [],
    isLookupLoading: false,
    lookupError: null,

    requestActivities: {},

    filters: {
      filterPo: null,
      startDate: null,
      endDate: null,
      search: null,
      status: null,
    },
  }),

  getters: {
    draftDto(state) {
      return {
        partnerId: state.draft.partnerId,
        companyId: state.draft.companyId,
        companyName: state.draft.companyName,
        companyLocation: state.draft.companyLocation,
        contactPersonName: state.draft.contactPersonName,
        contactPersonEmail: state.draft.contactPersonEmail,
        contactPersonPhone: state.draft.contactPersonPhone,
        trainingTypes: state.draft.trainingTypes,
        participantCount: Number(state.draft.participantCount),
        toyotaRepName: state.draft.toyotaRepName,
        toyotaRepPhone: state.draft.toyotaRepPhone,
        toyotaRepEmail: state.draft.toyotaRepEmail,
        additionalInfo: state.draft.additionalInfo,
        priceIndication: state.draft.priceIndication,
      };
    },
    getRequestLookup(state) {
      return state.requestLookup;
    },
    requestActivitiesByRequestId: (state) => (requestId) =>
      state.requestActivities[requestId] || [],
  },

  mutations: {
    SET_DRAFT(state, draft) {
      state.draft = { ...draft };
    },
    RESET_DRAFT(state, partnerId) {
      state.draft = getEmptyDraft(partnerId);
      state.saveError = null;
    },
    PATCH_DRAFT(state, patch) {
      state.draft = { ...state.draft, ...patch };
    },

    SET_SAVING(state, value) {
      state.isSaving = value;
    },
    SET_SAVE_ERROR(state, message) {
      state.saveError = message;
    },
    SET_ITEMS(state, items) {
      state.items = items;
    },
    SET_LOADING(state, value) {
      state.isLoading = value;
    },
    SET_LOAD_ERROR(state, message) {
      state.loadError = message;
    },

    SET_LOOKUP(state, items) {
      state.requestLookup = items;
    },
    SET_LOOKUP_LOADING(state, value) {
      state.isLookupLoading = value;
    },
    SET_LOOKUP_ERROR(state, msg) {
      state.lookupError = msg;
    },
    SET_REQUEST_ACTIVITIES(state, { requestId, activities }) {
      state.requestActivities = {
        ...state.requestActivities,
        [requestId]: activities || [],
      };
    },

    ADD_ACTIVITY_TO_REQUEST(state, activity) {
      if (!activity || !activity.requestId) return;

      const currentItems = state.requestActivities[activity.requestId] || [];

      state.requestActivities = {
        ...state.requestActivities,
        [activity.requestId]: [activity, ...currentItems],
      };
    },

    UPDATE_ACTIVITY_IN_REQUEST(state, { requestId, activity }) {
      if (!requestId || !activity) return;

      const currentItems = state.requestActivities[requestId] || [];

      state.requestActivities = {
        ...state.requestActivities,
        [requestId]: currentItems.map((item) =>
          item.id === activity.id ? activity : item,
        ),
      };
    },

    REMOVE_ACTIVITY_FROM_REQUEST(state, { requestId, activityId }) {
      if (!requestId || !activityId) return;

      const currentItems = state.requestActivities[requestId] || [];

      state.requestActivities = {
        ...state.requestActivities,
        [requestId]: currentItems.filter((item) => item.id !== activityId),
      };
    },

    MARK_REQUEST_SEEN(state, requestId) {
      const req = state.items.find((x) => x.id === Number(requestId));
      if (!req) return;
      req.hasUnreadUpdates = false;
    },

    SET_SEARCH(state, search) {
      state.filters.search = search;
    },

    UPSERT_ITEM(state, item) {
      if (!item || !item.id) return;

      const idx = state.items.findIndex((x) => x.id === item.id);
      if (idx === -1) {
        state.items.unshift(item);
        return;
      }

      const existing = state.items[idx] || {};
      const merged = { ...existing, ...item };

      Vue.set(state.items, idx, merged);
    },

    DELETE_ITEM(state, requestId) {
      const id = Number(requestId);
      state.items = state.items.filter((x) => x.id !== id);
    },

    SET_FILTERS(state, { start, end, filterPo, status }) {
      state.filters.filterPo = filterPo;
      state.filters.status = status;
      state.filters.startDate =
        start != null ? moment(start).format("YYYY-MM-DD") : null;
      state.filters.endDate =
        end != null ? moment(end).format("YYYY-MM-DD") : null;
    },
  },

  actions: {
    initDraft({ commit }, { partnerId }) {
      commit("RESET_DRAFT", partnerId);
    },

    patchDraft({ commit }, patch) {
      commit("PATCH_DRAFT", patch);
    },

    async fetchById({ commit }, { id, partnerId } = {}) {
      const requestId = Number(id);
      if (!requestId) throw new Error("requestId is required");

      const res = await axios.get(`/api/requests/${requestId}`, {
        params: partnerId ? { partnerId } : undefined,
      });

      commit("UPSERT_ITEM", res.data);
      return res.data;
    },

    async create({ commit, getters }) {
      commit("SET_SAVE_ERROR", null);
      commit("SET_SAVING", true);

      try {
        const dto = getters.draftDto;
        const res = await axios.post("/api/requests", dto);
        const created = res.data;

        return created;
      } catch (e) {
        const message =
          e?.response?.data?.errorMessage ||
          e?.response?.data ||
          e?.message ||
          "Opslaan mislukt";

        commit("SET_SAVE_ERROR", message);
        throw e;
      } finally {
        commit("SET_SAVING", false);
      }
    },
    async update({ commit, getters }, { id, partnerId }) {
      commit("SET_SAVE_ERROR", null);
      commit("SET_SAVING", true);

      try {
        const requestId = Number(id);
        const dto = getters.draftDto;

        const res = await axios.put(`/api/requests/${requestId}`, dto, {
          params: partnerId ? { partnerId } : undefined,
        });

        commit("UPSERT_ITEM", res.data);
        return res.data;
      } catch (e) {
        const message =
          e?.response?.data?.errorMessage ||
          e?.response?.data ||
          e?.message ||
          "Opslaan mislukt";

        commit("SET_SAVE_ERROR", message);
        throw e;
      } finally {
        commit("SET_SAVING", false);
      }
    },

    async deleteRequest({ commit }, { id, partnerId }) {
      const requestId = Number(id);

      await axios.delete(`/api/requests/${requestId}`, {
        params: partnerId ? { partnerId } : undefined,
      });

      commit("DELETE_ITEM", requestId);
    },
    async fetchRequests({ commit, state }, { partnerId } = {}) {
      commit("SET_LOAD_ERROR", null);
      commit("SET_LOADING", true);

      try {
        const params = { partnerId };
        if (state.filters.search) params.search = state.filters.search;
        if (
          state.filters.filterPo !== null &&
          state.filters.filterPo !== undefined
        ) {
          params.filterWithPoNumber = state.filters.filterPo === 1;
        }
        if (state.filters.status) params.status = state.filters.status;
        if (state.filters.startDate) params.startDate = state.filters.startDate;
        if (state.filters.endDate) params.endDate = state.filters.endDate;

        const res = await axios.get("/api/requests", { params });
        commit("SET_ITEMS", res.data);
        return res.data;
      } catch (e) {
        const message =
          e?.response?.data?.errorMessage ||
          e?.response?.data ||
          e?.message ||
          "Ophalen mislukt";

        commit("SET_LOAD_ERROR", message);
        throw e;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchRequestsLookup({ commit }, { partnerId, search }) {
      commit("SET_LOOKUP_ERROR", null);
      commit("SET_LOOKUP_LOADING", true);

      try {
        const res = await axios.get("/api/requests/lookup", {
          params: { partnerId, search },
        });

        commit("SET_LOOKUP", res.data);
        return res.data;
      } catch (e) {
        const message =
          e?.response?.data?.errorMessage ||
          e?.response?.data ||
          e?.message ||
          "Ophalen mislukt";

        commit("SET_LOOKUP_ERROR", message);
        throw e;
      } finally {
        commit("SET_LOOKUP_LOADING", false);
      }
    },
    async fetchRequestActivities({ commit }, requestId) {
      const res = await axios.get(`/api/requests/${requestId}/activities`);
      commit("SET_REQUEST_ACTIVITIES", {
        requestId,
        activities: res.data,
      });
    },
    async addRequestActivity({ commit }, payload) {
      const res = await axios.post(
        `/api/requests/${payload.requestId}/activities`,
        payload,
      );
      commit("ADD_ACTIVITY_TO_REQUEST", res.data);
      return res.data;
    },
    async updateRequestActivity({ commit }, payload) {
      const res = await axios.put(
        `/api/requests/${payload.requestId}/activities/${payload.activityId}`,
        {
          activityAt: payload.activityAt,
          text: payload.text,
        },
      );

      commit("UPDATE_ACTIVITY_IN_REQUEST", {
        requestId: payload.requestId,
        activity: res.data,
      });

      return res.data;
    },

    async deleteRequestActivity({ commit }, payload) {
      await axios.delete(
        `/api/requests/${payload.requestId}/activities/${payload.activityId}`,
      );

      commit("REMOVE_ACTIVITY_FROM_REQUEST", {
        requestId: payload.requestId,
        activityId: payload.activityId,
      });
    },
    async markRequestSeen({ commit }, requestId) {
      const id = Number(requestId);
      await axios.post(`/api/requests/${id}/read-marker`);
      commit("MARK_REQUEST_SEEN", id);
    },

    async searchRequestCourseCandidates(
      _,
      { requestId, query, from, to, companyId },
    ) {
      const { data } = await axios.get(
        `/api/requests/${requestId}/course-candidates`,
        {
          params: { q: query, from, to, companyId },
        },
      );
      return data || [];
    },

    async searchRequestOptionCandidates(
      _,
      { requestId, query, teacher, from, to },
    ) {
      const { data } = await axios.get(
        `/api/requests/${requestId}/option-candidates`,
        {
          params: { q: query, teacher, from, to },
        },
      );
      return data || [];
    },

    async linkCourseToRequest(
      { dispatch },
      { requestId, courseId, partnerId },
    ) {
      await axios.post(`/api/requests/${requestId}/courses/${courseId}`);
      await dispatch("fetchById", { id: requestId, partnerId });
      return;
    },

    async unlinkCourseFromRequest(
      { dispatch },
      { requestId, courseId, partnerId },
    ) {
      await axios.delete(`/api/requests/${requestId}/courses/${courseId}`, {
        params: partnerId ? { partnerId } : undefined,
      });

      await dispatch("fetchById", { id: requestId, partnerId });
    },

    async linkOptionToRequest(
      { dispatch },
      { requestId, optionId, partnerId },
    ) {
      await axios.post(`/api/requests/${requestId}/options/${optionId}`, null, {
        params: partnerId ? { partnerId } : undefined,
      });

      await dispatch("fetchById", { id: requestId, partnerId });
      return;
    },

    async unlinkOptionFromRequest(
      { dispatch },
      { requestId, optionId, partnerId },
    ) {
      await axios.delete(`/api/requests/${requestId}/options/${optionId}`, {
        params: partnerId ? { partnerId } : undefined,
      });

      await dispatch("fetchById", { id: requestId, partnerId });
    },

    async updateSearchString({ commit, dispatch }, searchObject) {
      commit("SET_SEARCH", searchObject.search);
      await dispatch("fetchRequests", { partnerId: searchObject.partnerId });
    },

    startEdit({ commit }, item) {
      if (!item) return;

      commit("SET_DRAFT", {
        partnerId: item.partnerId ?? null,
        companyId: item.companyId ?? null,
        companyName: item.companyName ?? "",
        companyLocation: item.companyLocation ?? "",
        contactPersonName: item.contactPersonName ?? "",
        contactPersonEmail: item.contactPersonEmail ?? "",
        contactPersonPhone: item.contactPersonPhone ?? "",
        trainingTypes: item.trainingTypes ?? "",
        participantCount: Number(item.participantCount ?? 1),
        toyotaRepName: item.toyotaRepName ?? "",
        toyotaRepPhone: item.toyotaRepPhone ?? "",
        toyotaRepEmail: item.toyotaRepEmail ?? "",
        additionalInfo: item.additionalInfo ?? "",
        priceIndication: item.priceIndication ?? "",
      });
      commit("SET_SAVE_ERROR", null);
    },

    async updateFilters({ commit, dispatch }, filters) {
      commit("SET_FILTERS", filters);
      await dispatch("fetchRequests", { partnerId: 1 });
    },

    async exportRequestsCsv(_, { partnerId, startDate, endDate }) {
      const response = await axios.get("/api/requests/export/csv", {
        params: {
          partnerId,
          startDate,
          endDate,
        },
        responseType: "blob",
      });

      return response;
    },
  },
};
